import type { GoogleFontFamilyRequest } from "../theme/googleFonts";
import {
	BUILDER_CANVAS_DARK_MODE_ATTR,
	BUILDER_THEME_SCOPE_CLASS,
	escapeThemeCssForStyleTag,
} from "../theme/tenantThemeCss";
import { CUSTOM_CODE_ROOT_ATTR } from "./customCodeCssScope";

/** Theme + page CSS the builder hands to sandboxed HTML custom-code iframes. */
export interface CustomCodeIframeStyles {
	pageCss: string;
	themeCss: string;
	/** Catalog families already known from Theme Manager. The iframe does not inherit parent font faces. */
	themeGoogleFonts?: readonly GoogleFontFamilyRequest[];
}

export type CustomCodeIframeColorMode = "light" | "dark";

export interface CustomCodeIframeThemeParts {
	bodyAttrs: string;
	headStyles: string;
	htmlAttrs: string;
	rootAttrs: string;
}

const EMPTY_PARTS: CustomCodeIframeThemeParts = {
	htmlAttrs: "",
	bodyAttrs: "",
	rootAttrs: "",
	headStyles: "",
};

/**
 * An iframe is its own document, so `--tt-*` vars and page CSS from the canvas never reach it.
 * Mirrors the live page: theme scope on the body, root excluded from theme tag typography.
 * No styles → empty parts, so the srcDoc is byte-identical to the unthemed one.
 */
export function buildCustomCodeIframeThemeParts(
	styles: CustomCodeIframeStyles | null | undefined,
	colorMode: CustomCodeIframeColorMode = "light"
): CustomCodeIframeThemeParts {
	const themeCss = styles?.themeCss.trim() ?? "";
	const pageCss = styles?.pageCss.trim() ?? "";
	if (!(themeCss || pageCss)) {
		return EMPTY_PARTS;
	}
	// Same body defaults as the live page, so dark-kit text never lands on a light canvas.
	const bodyDefaults = `body.${BUILDER_THEME_SCOPE_CLASS} { background: var(--tt-color-bg); color: var(--tt-color-fg); }`;
	const headStyles = [
		themeCss
			? `<style data-pb-iframe-theme>${escapeThemeCssForStyleTag(themeCss)} ${bodyDefaults}</style>`
			: "",
		pageCss
			? `<style data-pb-iframe-page-css>${escapeThemeCssForStyleTag(pageCss)}</style>`
			: "",
		// Page `body{min-height:100vh}` is sized to the whole page; in a per-block iframe it would
		// pin the reported height. Block CSS follows, so its own html/body rules still win.
		pageCss ? "<style>html, body { min-height: 0; height: auto; }</style>" : "",
	]
		.filter(Boolean)
		.join("\n  ");
	return {
		htmlAttrs: ` ${BUILDER_CANVAS_DARK_MODE_ATTR}="${colorMode}"`,
		bodyAttrs: ` class="${BUILDER_THEME_SCOPE_CLASS}"`,
		rootAttrs: ` ${CUSTOM_CODE_ROOT_ATTR}=""`,
		headStyles,
	};
}
